import { toNumber } from "features/shared/lib/formatters";

import type {
  ShadingSystemCatalogItem,
  WindowCatalogCreatePayload,
  WindowCatalogItem,
  WindowConfigSavePayload,
} from "../model/types";

import { type WindowConfigFormState } from "./windowConfigFieldDefinitions";
import { mapFormStateToPayload } from "./windowConfigForm";
import {
  findShadingCatalogItem,
  findWindowCatalogItem,
  getMatchingShadingCatalogItems,
  isShadingLocationSelected,
  isShadingSystemSelected,
} from "./windowConfigOptions";

type BuildWindowConfigSavePayloadOptions = {
  catalog: WindowCatalogItem[];
  formState: WindowConfigFormState;
  shadingCatalog?: ShadingSystemCatalogItem[];
};

type PayloadMode = "catalog-link" | "create-catalog" | "full-custom";

const mapFormStateToWindowTypeDb = (formState: WindowConfigFormState): WindowCatalogCreatePayload => {
  const payload: WindowCatalogCreatePayload = {
    frameTypeName: formState.frameType,
    windowTypeName: formState.windowTypeName,
  };

  const uValue = toNumber(formState.uValue);
  const gValueWindow = toNumber(formState.gValueWindow);
  const gValueDirectWindow = toNumber(formState.gValueDirectWindow);
  const gValueDiffuseWindow = toNumber(formState.gValueDiffuseWindow);
  const windowInfiltration = toNumber(formState.windowInfiltration);

  if (uValue !== undefined) payload.uValue = uValue;
  if (gValueWindow !== undefined) payload.gValueWindow = gValueWindow;
  if (gValueDirectWindow !== undefined) payload.gValueDirectWindow = gValueDirectWindow;
  if (gValueDiffuseWindow !== undefined) payload.gValueDiffuseWindow = gValueDiffuseWindow;
  if (windowInfiltration !== undefined) payload.windowInfiltration = windowInfiltration;

  return payload;
};

const resolveShadingCatalogItem = (
  formState: WindowConfigFormState,
  shadingCatalog: ShadingSystemCatalogItem[],
  catalog: WindowCatalogItem[]
) => {
  if (!isShadingSystemSelected(formState.shadingSystemType)) return undefined;

  const { items: matchedItems } = getMatchingShadingCatalogItems(shadingCatalog, catalog, {
    frameType: formState.frameType,
    shadingLocation: formState.shadingLocation,
    windowTypeName: formState.windowTypeName,
  });

  const matchedItem = matchedItems.find((item) => item.shadingSystemType === formState.shadingSystemType);
  if (matchedItem) return matchedItem;

  return findShadingCatalogItem(
    shadingCatalog,
    formState.shadingSystemType,
    undefined,
    isShadingLocationSelected(formState.shadingLocation) ? formState.shadingLocation : undefined
  );
};

const resolvePayloadMode = (
  formState: WindowConfigFormState,
  catalog: WindowCatalogItem[]
): undefined | { catalogItem?: WindowCatalogItem; mode: PayloadMode } => {
  const catalogItem = findWindowCatalogItem(catalog, formState.windowTypeName, formState.frameType);

  if (catalogItem) {
    return { catalogItem, mode: "catalog-link" };
  }

  if (formState.windowTypeName.trim() && formState.frameType.trim()) {
    return { mode: "create-catalog" };
  }

  return undefined;
};

const buildTechnologyPayload = (formState: WindowConfigFormState): WindowConfigSavePayload => ({
  ...mapFormStateToPayload(formState),
  frameType: formState.frameType || undefined,
  manuallyOverridden: true,
});

const applyShadingSelectionToPayload = (
  payload: WindowConfigSavePayload,
  shadingItem?: ShadingSystemCatalogItem
): WindowConfigSavePayload => {
  if (shadingItem) {
    return { ...payload, shadingSystemDbId: shadingItem.id };
  }

  return {
    ...payload,
    gValueDiffuseWindowShading: null,
    gValueDirectWindowShading: null,
    shadingSystemDbId: null,
    shadingSystemType: null,
  };
};

const buildCatalogLinkPayload = (
  formState: WindowConfigFormState,
  catalogItem: WindowCatalogItem,
  shadingItem?: ShadingSystemCatalogItem
): WindowConfigSavePayload =>
  applyShadingSelectionToPayload(
    {
      ...buildTechnologyPayload(formState),
      windowTypeDbId: catalogItem.id,
    },
    shadingItem
  );

const buildCreateCatalogPayload = (
  formState: WindowConfigFormState,
  shadingItem?: ShadingSystemCatalogItem
): WindowConfigSavePayload =>
  applyShadingSelectionToPayload(
    {
      ...buildTechnologyPayload(formState),
      windowTypeDb: mapFormStateToWindowTypeDb(formState),
    },
    shadingItem
  );

export const buildWindowConfigSavePayload = ({
  catalog,
  formState,
  shadingCatalog = [],
}: BuildWindowConfigSavePayloadOptions): undefined | WindowConfigSavePayload => {
  const shadingItem = resolveShadingCatalogItem(formState, shadingCatalog, catalog);
  const payloadMode = resolvePayloadMode(formState, catalog);
  if (!payloadMode) return;

  const { catalogItem, mode } = payloadMode;

  if (mode === "catalog-link" && catalogItem) {
    return buildCatalogLinkPayload(formState, catalogItem, shadingItem);
  }

  if (mode === "create-catalog") {
    return buildCreateCatalogPayload(formState, shadingItem);
  }
};
